// src/modules/app-views/invoices/pages/Document/modals/ItemModal/Step3.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Descriptions, Divider, Typography, Alert, Card, Tag, Space } from 'antd';
import { ShoppingCartOutlined, CalculatorOutlined, CheckCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';

const { Title, Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const SummaryStepContent = ({ form, calculatedTaxes, isNfce }) => {
  const description = form?.getFieldValue('description') || '';
  const productCode = form?.getFieldValue('productCode') || '';
  const ncm = form?.getFieldValue('ncm') || '';
  const cfop = form?.getFieldValue('cfop') || '';
  const unit = form?.getFieldValue('unit') || 'UN';
  const quantity = form?.getFieldValue('quantity') || 0;
  const unitValue = form?.getFieldValue('unitValue') || 0;
  const discount = form?.getFieldValue('discount') || { init: 'percentage', percentage: 0, value: 0 };

  // Calculate subtotal and apply discount value
  const itemSubtotal = financialService.roundCurrency(quantity * unitValue);
  const discountValue = discount.value || 0;
  const subtotal = financialService.roundCurrency(Math.max(0, itemSubtotal - discountValue));

  // For NFC-e there's no IPI, so don't add it to the total
  const totalWithTaxes = subtotal + (isNfce ? 0 : (calculatedTaxes?.ipi?.value || 0));

  return (
    <div>
      <Alert
        title={translateX('step3_summary_info')}
        description={translateX('step3_summary_description')}
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* Product Info */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          <ShoppingCartOutlined className="mr-2" />
          {description}
        </Title>
        <Space>
          {productCode && <Tag color="blue">{translateX('code')}: {productCode}</Tag>}
          {ncm && <Tag color="green">NCM: {ncm}</Tag>}
          {cfop && <Tag color="orange">CFOP: {cfop}</Tag>}
        </Space>
      </Card>

      {/* Values */}
      <Descriptions
        column={{ xs: 1, sm: 2, md: 3 }}
        size="small"
        bordered
      >
        <Descriptions.Item label={translateX('quantity')}>
          {financialService.formatQuantity(quantity)} {unit}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('unit_value')}>
          {financialService.formatCurrency(unitValue)}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('discount')}>
          {discount.percentage > 0 || discount.value > 0 ? (
            discount.init === 'percentage'
              ? financialService.formatPercentage(discount.percentage)
              : financialService.formatCurrency(discount.value)
          ) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('subtotal')}>
          <Text strong>{financialService.formatCurrency(subtotal)}</Text>
        </Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: '12px 0' }} />

      {/* Taxes Summary */}
      <Descriptions
        title={<><CalculatorOutlined className="mr-2" />{translateX('taxes')}</>}
        column={{ xs: 2, sm: isNfce ? 3 : 4 }}
        size="small"
      >
        <Descriptions.Item label="ICMS">
          {financialService.formatCurrency(calculatedTaxes?.icms?.value || 0)}
        </Descriptions.Item>
        <Descriptions.Item label="PIS">
          {financialService.formatCurrency(calculatedTaxes?.pis?.value || 0)}
        </Descriptions.Item>
        <Descriptions.Item label="COFINS">
          {financialService.formatCurrency(calculatedTaxes?.cofins?.value || 0)}
        </Descriptions.Item>
        {/* IPI only for NFe, not for NFC-e */}
        {!isNfce && (
          <Descriptions.Item label="IPI">
            {financialService.formatCurrency(calculatedTaxes?.ipi?.value || 0)}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider style={{ margin: '12px 0' }} />

      {/* Total */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        background: '#f6ffed',
        borderRadius: 6,
        border: '1px solid #b7eb8f',
      }}>
        <Title level={4} style={{ margin: 0 }}>
          {translateX('item_total')}
        </Title>
        <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
          {financialService.formatCurrency(totalWithTaxes)}
        </Title>
      </div>
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const summaryStep = ({ calculatedTaxes, isNfce }) => ({
  stepTitle: 'summary',
  stepTooltip: 'step3_tooltip',
  isConfirmStep: true,
  content: ({ form }) => (
    <SummaryStepContent
      form={form}
      calculatedTaxes={calculatedTaxes}
      isNfce={isNfce}
    />
  ),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default summaryStep;
